import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '../ui/Modal';
import { DemTypeahead, type DemTypeaheadOption } from './DemTypeahead';
import { addOrderAmendment } from '../../lib/demOrderBatches';
import { elapsedProcessingSeconds, addOrderProcessingSeconds } from '../../lib/demProcessingTime';
import { loadPoMappingContext, type DemPoItemOption } from '../../lib/demPoMapping';
import { fetchItemDetails } from '../../lib/demSkuMap';

type LineDraft = {
  id: string;
  itemCode: string;
  description: string;
  qty: string;
  uom: string;
};

type Props = {
  open: boolean;
  orderId: string;
  actor: string;
  onClose: () => void;
  onSaved: () => void;
};

function newLine(): LineDraft {
  return {
    id: crypto.randomUUID(),
    itemCode: '',
    description: '',
    qty: '',
    uom: 'PCS',
  };
}

export function OrderAmendModal({ open, orderId, actor, onClose, onSaved }: Props) {
  const [reason, setReason] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([newLine()]);
  const [itemOptions, setItemOptions] = useState<DemPoItemOption[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sessionStartedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!open) {
      sessionStartedAtRef.current = null;
      return;
    }
    sessionStartedAtRef.current = Date.now();
    setReason('');
    setLines([newLine()]);
    setError(null);
    setBusy(false);

    let cancelled = false;
    setLoadingItems(true);
    void loadPoMappingContext()
      .then((ctx) => {
        if (!cancelled) setItemOptions(ctx.itemOptions);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load items.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingItems(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  const itemTypeaheadOptions: DemTypeaheadOption[] = useMemo(
    () =>
      itemOptions.map((item) => ({
        value: item.code,
        label: item.code,
        sublabel: item.description,
      })),
    [itemOptions],
  );

  function updateLine(id: string, patch: Partial<LineDraft>) {
    setLines((prev) => prev.map((line) => (line.id === id ? { ...line, ...patch } : line)));
  }

  async function commitItemCode(lineId: string, code: string) {
    const next = code.trim().toUpperCase();
    updateLine(lineId, { itemCode: next });
    if (!next) {
      updateLine(lineId, { description: '', uom: 'PCS' });
      return;
    }
    try {
      const details = await fetchItemDetails([next]);
      const detail = details[next];
      const opt = itemOptions.find((i) => i.code.toUpperCase() === next);
      updateLine(lineId, {
        itemCode: next,
        description: detail?.description || opt?.description || '',
        uom: detail?.uom || 'PCS',
      });
    } catch {
      /* keep typed code */
    }
  }

  function removeLine(id: string) {
    setLines((prev) => {
      const next = prev.filter((row) => row.id !== id);
      return next.length === 0 ? [newLine()] : next;
    });
  }

  async function handleSubmit() {
    setBusy(true);
    setError(null);
    try {
      await addOrderAmendment({
        order_id: orderId,
        reason,
        created_by: actor,
        lines: lines.map((line) => ({
          item_code: line.itemCode,
          description: line.description,
          qty: Number(line.qty),
          uom: line.uom || 'PCS',
        })),
      });
      const amendSeconds = elapsedProcessingSeconds(sessionStartedAtRef.current);
      if (amendSeconds > 0) {
        await addOrderProcessingSeconds(orderId, amendSeconds);
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save add-on.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={busy ? () => undefined : onClose}
      title={`Add items — ${orderId}`}
      nested
      compactHeader
    >
      <div className="dem-order-amend">
        {error ? <p className="alert alert-error">{error}</p> : null}
        <p className="dem-order-amend__hint">
          Same order ID for NAV. Add-on exports as DET only after the original batch is exported.
        </p>
        <label className="field">
          <span className="field-label">Add-on reason</span>
          <input
            type="text"
            className="field-input"
            value={reason}
            disabled={busy}
            placeholder="e.g. Customer added items"
            onChange={(e) => setReason(e.target.value)}
            autoComplete="off"
          />
        </label>

        <div className="dem-order-amend__lines">
          <div className="dem-order-amend__lines-head" aria-hidden>
            <span>Item</span>
            <span>Description</span>
            <span>Qty</span>
            <span>UOM</span>
            <span />
          </div>
          {lines.map((line) => (
            <div key={line.id} className="dem-order-amend__line">
              <DemTypeahead
                value={line.itemCode}
                onChange={(value) => updateLine(line.id, { itemCode: value.toUpperCase() })}
                onCommit={(value) => void commitItemCode(line.id, value)}
                commitOnType={false}
                options={itemTypeaheadOptions}
                placeholder="Item code"
                ariaLabel="Item code"
                disabled={busy || loadingItems}
                tableMode
                inputClassName="field-input"
              />
              <p className="dem-order-amend__desc" title={line.description}>
                {line.description || '—'}
              </p>
              <input
                type="number"
                className="field-input"
                min={0}
                step="any"
                value={line.qty}
                onChange={(e) => updateLine(line.id, { qty: e.target.value })}
                disabled={busy}
                placeholder="0"
              />
              <p className="dem-order-amend__uom">{line.uom || '—'}</p>
              <button
                type="button"
                className="btn-secondary btn-sm"
                disabled={busy}
                title="Remove line"
                onClick={() => removeLine(line.id)}
              >
                ✕
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={busy}
            onClick={() => setLines((prev) => [...prev, newLine()])}
          >
            + Add line
          </button>
        </div>

        <div className="dem-order-amend__actions">
          <button type="button" className="btn-secondary btn-sm" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary btn-sm"
            disabled={busy || loadingItems}
            onClick={() => void handleSubmit()}
          >
            {busy ? 'Saving…' : 'Submit add-on'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
